import { AuthActionTypes, AuthUtils } from 'react-auth';
import toastr from 'toastr';
import { intl } from 'components/LocaleProvider';
import { Router } from 'next/router';
import get from 'base-ui/dist/underline/get';

import { Sentry } from 'root/utils/raven';
import messages from './messages';

/**
 * AuthMiddleware
 *
 * We intercept react-auth actions to add feedback messages to user.
 *
 */
const authMiddleware = ({ modalActions, dispatchUser, getState }) => (action) => {
  const setGAUserId = (id) => {
    if (id.length > 0) {
      ga('set', 'userId', id);
      ga('send', 'event', 'authentication', 'user-id available');
    } else {
      ga('set', 'userId', null);
      ga('send', 'event', 'authentication', 'user-id removed');
    }
  };

  if (action && action.type) {
    dispatchUser(action);
    switch (action.type) {
      case AuthActionTypes.AUTH_SIGN_UP: {
        const id = get(action.payload.user, '_id', '');
        const email = get(action.payload.user, 'email', '');
        const username = get(action.payload.user, 'username', '');
        Sentry.setUser({ email, id, username });
        Sentry.setTag('is_new_user', true);
        toastr.info(intl.formatMessage(messages.completeYourProfile), intl.formatMessage(messages.hello));
        toastr.info(intl.formatMessage(messages.confirmationEmailSent, { email }), intl.formatMessage(messages.attention));
        break;
      }
      case AuthActionTypes.AUTH_SIGN_IN: {
        toastr.info(action.payload.user.displayName, intl.formatMessage(messages.welcome));
        break;
      }
      case AuthActionTypes.AUTH_FETCHED_BY_TOKEN: {
        const id = get(action.payload.user, '_id', '');
        const email = get(action.payload.user, 'email', '');
        const username = get(action.payload.user, 'username', '');
        Sentry.setUser({ email, id, username });
        Sentry.setTag('is_new_user', false);
        setGAUserId(id);

        break;
      }
      case AuthActionTypes.AUTH_SIGN_OUT: {
        Sentry.setUser({});
        setGAUserId('');
        toastr.success(intl.formatMessage(messages.byeDetails), intl.formatMessage(messages.bye));
        break;
      }
      case AuthActionTypes.AUTH_AUTHENTICATE_SUCCESS: {
        toastr.info(action.payload.user.displayName, intl.formatMessage(messages.welcome));
        const id = get(action.payload.user, '_id', '');
        const email = get(action.payload.user, 'email', '');
        const username = get(action.payload.user, 'username', '');
        Sentry.setUser({ email, id, username });
        Sentry.setTag('is_new_user', false);
        setGAUserId(id);

        const { route } = getState.modalProps;
        if (route) {
          setTimeout(() => {
            Router.push(route);
          }, 750);
        } else {
          modalActions.hideModal();
        }
        break;
      }
      case AuthActionTypes.AUTH_UPDATE_PASSWORD: {
        toastr.info(intl.formatMessage(messages.passwordUpdated), intl.formatMessage(messages.done));
        break;
      }
      case AuthActionTypes.AUTH_USER_CONFIRMATION_TOKEN_VALIDATED: {
        toastr.info(intl.formatMessage(messages.readyToNavigate), intl.formatMessage(messages.attention));
        toastr.success(intl.formatMessage(messages.accountConfirmed), intl.formatMessage(messages.done));
        break;
      }
      case AuthActionTypes.AUTH_USER_CONFIRMATION_TOKEN_EXPIRED: {
        toastr.error(intl.formatMessage(messages.expiredTokenAccountConfirmation));
        break;
      }
      case AuthActionTypes.AUTH_LINK_GOOGLE_REQUEST_SUCCESS: {
        toastr.success(intl.formatMessage(messages.linkProviderSuccess, { provider: 'Google+' }));
        break;
      }
      case AuthActionTypes.AUTH_LINK_GOOGLE_REQUEST_ERROR: {
        toastr.error(intl.formatMessage(messages.error));
        break;
      }
      case AuthActionTypes.AUTH_LINK_FACEBOOK_REQUEST_SUCCESS: {
        toastr.success(intl.formatMessage(messages.linkProviderSuccess, { provider: 'Facebook' }));
        break;
      }
      case AuthActionTypes.AUTH_LINK_FACEBOOK_REQUEST_ERROR: {
        toastr.error(intl.formatMessage(messages.error));
        break;
      }
      case AuthActionTypes.AUTH_UNLINK_GOOGLE_REQUEST_SUCCESS: {
        toastr.success(intl.formatMessage(messages.unlinkProviderSuccess, { provider: 'Google+' }));
        break;
      }
      case AuthActionTypes.AUTH_UNLINK_GOOGLE_REQUEST_ERROR: {
        toastr.error(intl.formatMessage(messages.error));
        break;
      }
      case AuthActionTypes.AUTH_UNLINK_FACEBOOK_REQUEST_SUCCESS: {
        toastr.success(intl.formatMessage(messages.unlinkProviderSuccess, { provider: 'Facebook' }));
        break;
      }
      case AuthActionTypes.AUTH_UNLINK_FACEBOOK_REQUEST_ERROR: {
        toastr.error(intl.formatMessage(messages.error));
        break;
      }
      case AuthActionTypes.AUTH_ERROR: {
        const { error } = action.payload;
        const title = intl.formatMessage(messages.error);
        const subtitle = String(error).match('NetworkError')
          ? intl.formatMessage(messages.networkError)
          : get(error, 'data.message', get(error, 'message', error));
        toastr.error(subtitle, title);
        if (error && error.data && error.data.invalid_session) {
          AuthUtils.removeUserToken();

          // eslint-disable-next-line no-unused-vars
          const openModal = (modalType) => {
            const content = {};
            const width = '420px';
            const height = 'min-content';

            try {
              modalActions.showModal({
                modalProps: {
                  openModal,
                  height,
                  width,
                  styles: {
                    content
                  }
                },
                modalType
              });
            } catch (err) {
              // eslint-disable-next-line no-console
              console.log(err);
            }
          };
          openModal('signInModal');
        }
        break;
      }
      default:
        break;
    }
  }
  return action;
};

export default authMiddleware;
